import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from './ui/button';
import axios from 'axios';
import { 
  BarChart3, 
  Sun, 
  Moon, 
  LogOut, 
  Settings,
  Shield,
  Bell,
  Check,
  X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NavBar = () => {
  const { user, logout } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  
  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications/unread-count`);
      setUnreadCount(res.data.count);
    } catch (err) {
      console.error('Unread count fetch failed:', err);
    }
  }, []);
  
  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications`);
      setNotifications(res.data);
    } catch (err) {
      console.error('Notifications fetch failed:', err);
    }
  };
  
  useEffect(() => {
    if (user) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user, fetchUnreadCount]);
  
  const handleBellClick = () => {
    if (!showNotifications) fetchNotifications();
    setShowNotifications(!showNotifications);
  };
  
  const markAsRead = async (id) => {
    await axios.put(`${API}/notifications/${id}/read`);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };
  
  const markAllRead = async () => {
    await axios.put(`${API}/notifications/read-all`);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };
  
  const handleLogout = () => {
    logout();
    navigate('/');
  };
  
  const typeColors = {
    success: 'text-emerald-400',
    info: 'text-blue-400',
    warning: 'text-amber-400',
    error: 'text-red-400'
  };
  
  const formatTime = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return language === 'tr' ? 'Az önce' : 'Just now';
    if (mins < 60) return `${mins} ${language === 'tr' ? 'dk önce' : 'min ago'}`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} ${language === 'tr' ? 'sa önce' : 'hr ago'}`;
    return `${Math.floor(hours / 24)} ${language === 'tr' ? 'gün önce' : 'days ago'}`;
  };
  
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-xl border-b border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-6">
            <Link to="/dashboard" className="flex items-center space-x-2">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold text-white">GDMII</span>
            </Link>
            
            <div className="hidden md:flex items-center space-x-1">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white hover:bg-slate-800">
                  {t('dashboard')}
                </Button>
              </Link>
              <Link to="/assessments">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white hover:bg-slate-800">
                  {t('assessments')}
                </Button>
              </Link>
              <Link to="/reports">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white hover:bg-slate-800">
                  {t('reports')}
                </Button>
              </Link>
              <Link to="/companies">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white hover:bg-slate-800">
                  {t('companies')}
                </Button>
              </Link>
              {user?.role === 'admin' && (
                <Link to="/admin">
                  <Button variant="ghost" size="sm" className="text-purple-400 hover:text-purple-300 hover:bg-purple-500/10">
                    <Shield className="h-4 w-4 mr-1" />
                    Admin
                  </Button>
                </Link>
              )}
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {/* Notifications Bell */}
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBellClick}
                className="text-slate-400 hover:text-white relative"
                data-testid="notification-bell"
              >
                <Bell size={18} />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 rounded-full text-[10px] font-bold text-white flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Button>
              
              {showNotifications && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-slate-900 border border-slate-700 rounded-lg shadow-xl z-50 overflow-hidden" data-testid="notification-panel">
                  <div className="flex items-center justify-between p-3 border-b border-slate-700">
                    <span className="text-sm font-semibold text-white">
                      {language === 'tr' ? 'Bildirimler' : 'Notifications'}
                    </span>
                    <div className="flex gap-1">
                      {unreadCount > 0 && (
                        <button onClick={markAllRead} className="text-xs text-blue-400 hover:text-blue-300">
                          <Check size={14} className="inline mr-0.5" />
                          {language === 'tr' ? 'Tümünü Oku' : 'Read All'}
                        </button>
                      )}
                      <button onClick={() => setShowNotifications(false)} className="text-slate-500 hover:text-white ml-2">
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="max-h-72 overflow-y-auto">
                    {notifications.length > 0 ? notifications.map(n => (
                      <div
                        key={n.id}
                        className={`p-3 border-b border-slate-800 hover:bg-slate-800/50 cursor-pointer transition-colors ${!n.read ? 'bg-slate-800/30' : ''}`}
                        onClick={() => {
                          markAsRead(n.id);
                          if (n.link) { navigate(n.link); setShowNotifications(false); }
                        }}
                      >
                        <div className="flex items-start justify-between">
                          <p className={`text-sm font-medium ${typeColors[n.type] || 'text-white'}`}>{n.title}</p>
                          {!n.read && <span className="w-2 h-2 bg-blue-400 rounded-full mt-1.5 flex-shrink-0 ml-2" />}
                        </div>
                        <p className="text-xs text-slate-400 mt-0.5">{n.message}</p>
                        <p className="text-[10px] text-slate-600 mt-1">{formatTime(n.created_at)}</p>
                      </div>
                    )) : (
                      <div className="p-6 text-center">
                        <Bell className="h-8 w-8 text-slate-700 mx-auto mb-2" />
                        <p className="text-sm text-slate-500">
                          {language === 'tr' ? 'Bildirim yok' : 'No notifications'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleTheme}
              data-testid="navbar-theme-toggle"
              className="text-slate-400 hover:text-white"
            >
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              data-testid="navbar-language-toggle"
              className="text-slate-400 hover:text-white font-medium"
            >
              {language === 'tr' ? 'EN' : 'TR'}
            </Button>
            
            <Link to="/settings">
              <Button variant="ghost" size="sm" data-testid="navbar-settings-btn" className="text-slate-400 hover:text-white">
                <Settings size={18} />
              </Button>
            </Link>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              data-testid="navbar-logout-btn"
              className="text-slate-400 hover:text-white"
            >
              <LogOut size={18} />
            </Button>
            
            {user && (
              <div className="pl-2 border-l border-slate-700">
                <span className="text-sm text-slate-400 hidden sm:inline-block max-w-[160px] truncate" data-testid="navbar-user-name">{user.full_name}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default NavBar;
